// Funções auxiliares para processamento de dados do Dashboard
import { PLAGUE_MAP, MONTHS, INFESTATION_LEVELS } from "./constants";
import { storageService } from "../services/storageService";
import logger from "../../../utils/logger";

/**
 * Processa e normaliza dados de previsão da API
 * @param {Object} apiData - Dados recebidos da API
 * @returns {Array} Array de dados processados
 */
export const processPredictionData = (apiData) => {
  const currentRegion = storageService.getRegion();
  let predictionData = [];

  if (apiData?.dados) {
    if (Array.isArray(apiData.dados)) {
      predictionData = apiData.dados;
    } else if (typeof apiData.dados === "object") {
      if (currentRegion && apiData.dados[currentRegion]) {
        predictionData = Array.isArray(apiData.dados[currentRegion])
          ? apiData.dados[currentRegion]
          : [];
      } else {
        const allRegions = Object.values(apiData.dados).filter(item => Array.isArray(item));
        predictionData = allRegions.flat();
      }
    }
  } else if (Array.isArray(apiData)) {
    predictionData = apiData;
  }

  // Normalizar dados
  const datePrediction = apiData?.datePrediction;
  predictionData = predictionData.map((item) => {
    // Adicionar period se não existir
    if (!item.period && datePrediction) {
      const date = new Date(datePrediction);
      const month = MONTHS[date.getMonth()];
      const year = date.getFullYear();
      const semester = date.getMonth() < 6 ? 1 : 2;
      item.period = `Sem ${semester} ${month}. ${year}`;
    }

    // Mapear plague do scientific_name se não existir
    if (!item.plague && item.scientific_name) {
      item.plague = PLAGUE_MAP[item.scientific_name] || item.scientific_name;
    }

    // Mapear plant
    if (!item.plant) {
      item.plant = item.plant_type || item.material || "N/A";
    }

    // Adicionar level se não existir
    if (!item.level && item.diagnostico) {
      item.level = item.diagnostico;
    }

    return item;
  });

  return predictionData;
};

/**
 * Obtém ocorrências únicas baseadas em plot, region e scientific_name
 * @param {Array} data - Array de dados de previsão
 * @returns {Array} Array de ocorrências únicas
 */
export const getUniqueOccurrences = (data) => {
  return data.reduce((acc, current) => {
    const existing = acc.find(
      (item) =>
        item.plot === current.plot &&
        item.region === current.region &&
        item.scientific_name === current.scientific_name
    );

    if (!existing) {
      acc.push(current);
    }

    return acc;
  }, []);
};

/**
 * Filtra ocorrências por região e plots válidos
 * @param {Array} occurrences - Ocorrências únicas
 * @param {Array} plots - Array de plots válidos
 * @returns {Array} Ocorrências filtradas
 */
export const filterOccurrencesByPlots = (occurrences, plots) => {
  const currentRegion = storageService.getRegion();
  
  logger.debug("Filtrando ocorrências", {
    occurrencesCount: occurrences.length,
    plotsCount: plots?.length || 0,
    currentRegion
  });

  // Se não há plots ou plots está vazio, retornar apenas ocorrências da região
  if (!Array.isArray(plots) || plots.length === 0) {
    logger.debug("Nenhum plot disponível, retornando ocorrências da região", {
      filteredCount: occurrences.filter(oc => oc.region === currentRegion).length
    });
    return occurrences.filter(oc => oc.region === currentRegion);
  }

  // Filtrar plots da região atual
  const plotsDaRegiao = plots.filter(p => p.region === currentRegion);
  
  logger.debug("Plots da região", {
    totalPlots: plots.length,
    plotsDaRegiao: plotsDaRegiao.length
  });
  
  // Se não há plots da região, retornar ocorrências da região sem filtro de plot
  if (plotsDaRegiao.length === 0) {
    logger.warn("Nenhum plot da região encontrado, retornando todas as ocorrências da região");
    return occurrences.filter(oc => oc.region === currentRegion);
  }

  // Filtrar ocorrências que correspondem aos plots da região
  const filtered = occurrences.filter(oc => {
    // Primeiro verificar se a ocorrência é da região correta
    if (oc.region !== currentRegion) {
      return false;
    }

    // Verificar se existe um plot correspondente
    const plotMatch = plotsDaRegiao.some(plot => {
      // Comparar plot como string para evitar problemas de tipo
      const plotNumber = String(plot.plot || plot.plot_number || "");
      const occurrencePlot = String(oc.plot || "");
      return plotNumber === occurrencePlot && plotNumber !== "";
    });

    return plotMatch;
  });

  logger.debug("Ocorrências filtradas", {
    originalCount: occurrences.length,
    filteredCount: filtered.length,
    plotsDaRegiaoCount: plotsDaRegiao.length
  });

  // Se após filtrar não encontrou nenhuma correspondência, mas há ocorrências da região,
  // retornar todas as ocorrências da região (pode ser que os plots não estejam sincronizados)
  if (filtered.length === 0 && occurrences.length > 0) {
    const regionOccurrences = occurrences.filter(oc => oc.region === currentRegion);
    logger.warn("Nenhuma correspondência encontrada entre plots e ocorrências. Retornando todas as ocorrências da região.", {
      regionOccurrencesCount: regionOccurrences.length,
      plotsDaRegiaoCount: plotsDaRegiao.length
    });
    return regionOccurrences;
  }

  return filtered;
};

/**
 * Processa contadores de infestação por nível
 * @param {Array} data - Dados de ocorrências
 * @returns {Object} Objeto com contadores e dados ordenados
 */
export const populateCounters = (data) => {
  const currentRegion = storageService.getRegion();
  let low_counter = 0;
  let med_counter = 0;
  let high_counter = 0;
  let hectar_counter = 0;
  let newVector = [];

  data.forEach((oc) => {
    if (oc.region === currentRegion) {
      const probability = parseInt(oc.prob);
      
      if (probability >= INFESTATION_LEVELS.LOW.min && probability <= INFESTATION_LEVELS.LOW.max) {
        newVector.push(oc);
        low_counter += 1;
      } else if (probability >= INFESTATION_LEVELS.MEDIUM.min && probability <= INFESTATION_LEVELS.MEDIUM.max) {
        med_counter += 1;
        newVector.push(oc);
      } else if (probability >= INFESTATION_LEVELS.HIGH.min) {
        high_counter += 1;
        hectar_counter += oc.area || 0;
        newVector.push(oc);
      }
    }
  });

  // Ordenar por probabilidade (maior primeiro)
  newVector.sort((a, b) => b.prob - a.prob);

  return {
    low: low_counter,
    medium: med_counter,
    high: high_counter,
    hectar: hectar_counter,
    sortedOccurrences: newVector
  };
};

/**
 * Calcula percentuais de infestação por praga
 * @param {Array} data - Dados de ocorrências
 * @param {Array} plagueNames - Array de nomes de pragas
 * @returns {Array} Array com percentuais por praga
 */
export const calculateInfestations = (data, plagueNames) => {
  const currentRegion = storageService.getRegion();
  const plaguesPercentual = [];

  plagueNames.forEach((plague) => {
    let count = 0;
    let all_percentual = 0;

    data.forEach((dt) => {
      if (dt.plague === plague.name && dt.region === currentRegion) {
        count += 1;
        all_percentual += dt.prob;
      }
    });

    if (count > 0) {
      plaguesPercentual.push({
        percentual: all_percentual / count,
        plague: plague.name,
      });
    }
  });

  return plaguesPercentual;
};

/**
 * Extrai municípios únicos dos dados
 * @param {Array} data - Dados de ocorrências
 * @returns {Array} Array de municípios únicos
 */
export const extractMunicipalities = (data) => {
  const currentRegion = storageService.getRegion();
  const municipios = [];

  data.forEach((objeto) => {
    if (
      !municipios.includes(objeto.municipality) &&
      objeto.region === currentRegion
    ) {
      municipios.push(objeto.municipality);
    }
  });

  return municipios;
};

/**
 * Filtra plots por município/UGB
 * @param {Array} occurrencesSort - Ocorrências ordenadas
 * @param {string} municipality - Município/UGB selecionado
 * @returns {Array} Plots filtrados
 */
export const filterPlotsByMunicipality = (occurrencesSort, municipality) => {
  const currentRegion = storageService.getRegion();
  const plots = [];

  occurrencesSort.forEach((occurrence) => {
    if (
      occurrence.region === currentRegion &&
      (occurrence.municipality === municipality ||
        occurrence.ugb === municipality)
    ) {
      plots.push(occurrence);
    }
  });

  return plots;
};

